import React, { Component } from 'react';
import { Route, Switch } from 'react-router-dom';
import store from './redux/store.js';
import CssBaseline from '@mui/material/CssBaseline';
import { GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Theme, Loader } from '@simplybenefits/sb-ui-library';
import CacheBuster from 'react-cache-buster';
import version from '../package.json';

import PrivateRoute from './PrivateRoute.js';
import Login from './containers/Login/index.js';
import Dashboard from './containers/Dashboard/index.js';
import Advisors from './containers/Advisor/AdvisorList.js';
import Advisor from './containers/Advisor/Dashboard.js';
import Adjudication from './containers/Adjudication/index.js';
import Administration from './containers/Administration/index.js';
import ChangeRequest from './containers/Administration/components/ChangeRequest.js';
import Notifications from './containers/Notifications/index.js';
import Metrics from './containers/Metrics/index.js';
import AdjudicateClaim from './containers/Adjudication/Adjudicate.js';
import AdjudicationCreateClaim from './containers/Adjudication/CreateClaim/index.js';
import AdjudicationDentalTransactions from './containers/Adjudication/Dental/index.js';
import AdjudicationClaimsList from './containers/Adjudication/ClaimsList.js';
import AdjudicationPredeterminationsList from './containers/Adjudication/Predeterminations.js';
import Users from './containers/User/UserList.js';
import NotFound from './containers/NotFound/index.js';
import InternalError from './containers/InternalError/index.js';
import Forbidden from './containers/Forbidden/index.js';
import User from './containers/User/Dashboard.js';
import ChangePassword from './containers/ChangePassword/index.js';
import QuotesForReview from './containers/Quotes/QuotesForReview.js';
import Quote from './containers/Quotes/Quote.js';
import Revision from './containers/Account/Revision/index.js';
import Amendments from './containers/Account/Amendments/index.js';
import Amendment from './containers/Account/Amendments/Amendment.js';
import Accounts from './containers/Account/AccountList.js';
import Account from './containers/Account/Dashboard.js';
import Renewals from './containers/Account/Renewals/index.js';
import Renewal from './containers/Account/Renewals/Renewal.js';
import Reports from './containers/Reports/index.js';
import Uploads from './containers/Uploads/index.js';
import Permissions from './containers/Permissions/index.js';
import Notifier from './components/Notifier.js';
import * as session from './services/session.js';
import { getMaster } from './actions/master.js';
import { connect } from 'react-redux';
import jiraBugButton from './jiraBugButton.js';

const theme = Theme.themeWithWhitelabel(null);

class App extends Component {
    state = {
        loader: false,
        token: session.getToken()
    };

    componentDidMount() {
        jiraBugButton();

        if (window.location.href.indexOf('login-complete?token=') > -1) {
            const temp = window.location.href.split('token=');
            session.setToken(temp[1]);

            window.location.href = '/';
            return;
        }

        this._pullData().then(() => {
            this.setState({
                loading: false
            });
        });
    }

    _pullData = async () => {
        if (this.state.token) {
            this.setState({
                loading: true
            });

            await store.dispatch(getMaster());

            this.setState({
                loading: false
            });
        }
    };

    render() {
        const globalStyles = {
            '*::-webkit-scrollbar': {
                height: 15,
                width: 15
            },
            '*::-webkit-scrollbar-thumb': {
                backgroundColor: Theme.colors.surface,
                borderRadius: 10,
                boxShadow: 'inset 0 0 2px rgba(0, 0, 0, .5)'
            },
            '*::-webkit-scrollbar-track': {
                backgroundColor: Theme.colors.backgroundGrey,
                borderRadius: 10,
                boxShadow: 'inset 0 0 2px rgba(0, 0, 0, 0.2)'
            }
        };

        const styles = {
            main: {
                height: 'auto',
                margin: 0,
                minHeight: '100vh',
                padding: 0,
                position: 'relative'
            }
        };

        if (this.state.loader?.isLoading || (this.state.token && !store.getState().master.email)) {
            return (
                <CacheBuster
                    currentVersion={ version.version }
                    isEnabled={ true } // If false, the library is disabled.
                    loadingComponent={ <Loader isContainer={true} /> } // If not pass, nothing appears at the time of new version check.
                >
                    <ThemeProvider theme={ theme }>
                        <CssBaseline />
                        <main style={ styles.main }>
                            <Loader isContainer/>
                        </main>
                    </ThemeProvider>
                </CacheBuster>
            );
        }

        return (
            <CacheBuster
                currentVersion={ version.version }
                isEnabled={ true } // If false, the library is disabled.
                loadingComponent={ <Loader isContainer={true} /> } // If not pass, nothing appears at the time of new version check.
            >
                <ThemeProvider theme={ theme }>
                    <CssBaseline />
                    <GlobalStyles styles={globalStyles}/>
                    <main style={ styles.main }>
                        <Switch>
                            <Route exact path="/login" component={Login} />
                            <PrivateRoute exact path="/" component={Dashboard} />
                            <PrivateRoute exact path="/advisors" component={Advisors} />
                            <PrivateRoute exact path="/adjudication" component={Adjudication} />
                            <PrivateRoute exact path="/administration" component={Administration} />
                            <PrivateRoute exact path="/administration/changeRequests/:changeRequestID" component={ChangeRequest} />
                            <PrivateRoute exact path="/notifications" component={Notifications} />
                            <PrivateRoute exact path="/metrics" component={Metrics} />
                            <PrivateRoute exact path="/adjudication/createClaim" component={AdjudicationCreateClaim} />
                            <PrivateRoute exact path="/adjudication/simplyDental" component={AdjudicationDentalTransactions} />
                            <PrivateRoute exact path="/adjudication/simplyDental/:type/:cert/:claimID/:manualTransactionUid" component={AdjudicationDentalTransactions} />
                            <PrivateRoute exact path="/adjudication/claims" component={AdjudicationClaimsList} />
                            <PrivateRoute exact path="/adjudication/predeterminations" component={AdjudicationPredeterminationsList} />
                            <PrivateRoute exact path="/adjudication/:claimID" component={AdjudicateClaim} />
                            <PrivateRoute exact path="/members" component={Users} />
                            <PrivateRoute exact path="/accounts" component={Accounts} />
                            <PrivateRoute exact path="/quotes-for-review" component={QuotesForReview} />
                            <PrivateRoute exact path="/advisors/:advisorID/quotes/:quoteID" component={Quote} />
                            <PrivateRoute exact path="/quotes/review/:quoteID" component={Quote} />
                            <PrivateRoute exact path="/accounts/:accountID" component={Account} />
                            <PrivateRoute exact path="/accounts/:accountID/revision/:revisionID" component={Revision} />
                            <PrivateRoute exact path="/accounts/:accountID/amendments" component={Amendments} />
                            <PrivateRoute exact path="/accounts/:accountID/amendments/:amendmentID" component={Amendment} />
                            <PrivateRoute exact path="/accounts/:accountID/renewals" component={Renewals} />
                            <PrivateRoute exact path="/accounts/:accountID/renewals/:renewalID" component={Renewal} />
                            <PrivateRoute exact path="/advisors/:advisorID" component={Advisor} />
                            <PrivateRoute exact path="/members/:userID" component={User} />
                            <PrivateRoute exact path="/settings/security" component={ChangePassword} />
                            <PrivateRoute exact path="/reports" component={Reports} />
                            <PrivateRoute exact path="/uploads" component={Uploads} />
                            <PrivateRoute exact path="/permissions" component={Permissions} />
                            <PrivateRoute exact path="/accounts/:accountID/history/file/:fileType/:filename" component={ Account } />
                            <Route path="/403" component={Forbidden} />
                            <Route path="/error" component={InternalError} />
                            <Route component={NotFound} />
                        </Switch>
                        <Notifier />
                    </main>
                </ThemeProvider>
            </CacheBuster>
        );
    }
}

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader
});

export default (connect(mapStateToProps)(App));